import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spin, Tooltip } from 'antd';
import {
  APP_HEADER_HEIGHT,
  NAV_SECTION_HEIGHT,
} from 'components/organisms/oneplatform/patient-chart-nav-section/constants';

import s from './pod-tooltip.module.scss';

import type {
  IClinicDetail,
  IPodAssignmentsResponse,
} from 'utils/hooks/rpmOverviewDashboard/usePodAssignments';

interface IPodDetailTooltipProps {
  podSection: 'primary' | 'secondary' | 'tertiary' | 'outside';
  podName: string | null;
  children: React.ReactElement;
  podAssignments: IPodAssignmentsResponse | undefined;
  podAssignmentsError: boolean;
  podAssignmentsLoading?: boolean;
  patientClinicGuid?: string | null;
  priorityColor?: string;
}

const PodDetailTooltip: React.FC<IPodDetailTooltipProps> = ({
  podSection,
  podName,
  children,
  podAssignments,
  podAssignmentsError,
  podAssignmentsLoading,
  patientClinicGuid,
  priorityColor,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;

    const handleScroll = () => {
      if (!containerRef.current) return;
      const { top } = containerRef.current.getBoundingClientRect();
      if (top < APP_HEADER_HEIGHT + NAV_SECTION_HEIGHT) {
        setOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [open]);

  useEffect(() => {
    if (podAssignmentsError || !podAssignments?.pods?.length || !podName) {
      setOpen(false);
    }
  }, [podAssignmentsError, podAssignments?.pods?.length, podName]);

  if (podAssignmentsError || !podAssignments?.pods?.length) {
    return <>{children}</>;
  }

  if (!podName) {
    return <>{children}</>;
  }

  const matchingPod = podAssignments.pods.find(
    (p) => p.priority === podSection && p.podName === podName,
  );

  if (!matchingPod) {
    return <>{children}</>;
  }

  const clinics: IClinicDetail[] = matchingPod.clinics ?? [];

  const tooltipContent = (
    <div data-cy="pod-tooltip" data-testid="pod-tooltip">
      {podAssignmentsLoading ? (
        <div className={s.loading}>
          <Spin size="small" />
        </div>
      ) : (
        <>
          <div className={s['pod-name']}>{matchingPod.podName}</div>

          <div className={s['priority-line']}>
            {t('labels.pods.tooltip.priority-label')}{' '}
            <span style={{ color: priorityColor ?? 'inherit' }}>
              {t(`labels.pods.tier.${podSection}`)}
            </span>
          </div>

          {clinics.length > 0 && (
            <div className={s['clinic-label']}>
              {t('labels.pods.tooltip.clinic-label')}
              <ul className={s['clinic-list']}>
                {clinics.map((clinic) => (
                  <li key={clinic.guid} className={s['clinic-item']}>
                    {clinic.name}
                    {clinic.state && (
                      <em className={s['clinic-state']}> ({clinic.state})</em>
                    )}
                    {clinics.length > 1 &&
                      patientClinicGuid &&
                      clinic.guid === patientClinicGuid && (
                        <span className={s['patient-clinic-label']}>
                          {' - '}
                          {t('labels.pods.tooltip.patient-clinic')}
                        </span>
                      )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <span ref={containerRef}>
      <Tooltip
        open={open}
        onOpenChange={setOpen}
        title={tooltipContent}
        trigger={['click']}
        placement="bottom"
        classNames={{ root: s['pod-detail-tooltip'] }}
        arrow={false}
      >
        {children}
      </Tooltip>
    </span>
  );
};

export default PodDetailTooltip;
